import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type LegalKey = 'terms' | 'privacy';
const LEGAL_CONTENT: Record<LegalKey, {
  title: string;
  sections: {
    heading?: string;
    body: string;
  }[];
}> = {
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      body: 'Bienvenida a Legado Salud. Al acceder, descargar o utilizar nuestro ebook "El Protocolo Meno-Metabólico" y cualquier contenido relacionado, aceptas los siguientes términos y condiciones. Si no estás de acuerdo, te pedimos no continuar con la compra ni con el uso del material.'
    }, {
      heading: '1. Naturaleza del contenido',
      body: 'El material ofrecido tiene fines exclusivamente educativos e informativos. No constituye consejo médico, diagnóstico ni tratamiento. Antes de iniciar cualquier plan de alimentación, suplementación o cambio de hábitos, consulta a un profesional de la salud, especialmente si tienes condiciones médicas preexistentes, estás embarazada o tomas medicamentos.'
    }, {
      heading: '2. Uso personal',
      body: 'La compra otorga una licencia personal, intransferible y no exclusiva para uso individual. Está prohibido copiar, redistribuir, revender, publicar o compartir el contenido de forma total o parcial sin autorización por escrito.'
    }, {
      heading: '3. Pagos y entrega',
      body: 'El pago es único y la entrega del ebook se realiza de forma digital e inmediata tras la confirmación de la transacción. Es responsabilidad del usuario proporcionar un correo electrónico válido para recibir el acceso.'
    }, {
      heading: '4. Política de reembolso',
      body: 'Debido a la naturaleza digital del producto, las ventas son definitivas. Si tienes algún inconveniente con la descarga, contáctanos para encontrar una solución.'
    }, {
      heading: '5. Limitación de responsabilidad',
      body: 'Los resultados pueden variar entre personas. Legado Salud no garantiza resultados específicos y no se hace responsable por decisiones tomadas con base en el contenido del material.'
    }, {
      heading: '6. Modificaciones',
      body: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las versiones vigentes serán las publicadas en este sitio.'
    }]
  },
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      body: 'En Legado Salud respetamos tu privacidad y nos comprometemos a proteger la información personal que compartes con nosotros. Esta política explica qué datos recopilamos, cómo los usamos y qué derechos tienes sobre ellos.'
    }, {
      heading: '1. Información que recopilamos',
      body: 'Recopilamos los datos que nos proporcionas voluntariamente al realizar una compra, como nombre y correo electrónico. Los pagos son procesados por proveedores externos seguros; no almacenamos información de tarjetas en nuestros servidores.'
    }, {
      heading: '2. Uso de la información',
      body: 'Utilizamos tus datos para procesar tu compra, entregar el producto digital adquirido, enviarte información relacionada con el contenido y, ocasionalmente, comunicaciones sobre nuevos materiales o recursos que puedan interesarte.'
    }, {
      heading: '3. Cookies y tecnologías similares',
      body: 'Este sitio puede utilizar cookies y herramientas de análisis para mejorar la experiencia de navegación y entender cómo se usa el contenido. Puedes desactivarlas desde la configuración de tu navegador.'
    }, {
      heading: '4. Compartir información con terceros',
      body: 'No vendemos ni alquilamos tus datos personales. Solo compartimos información con proveedores estrictamente necesarios para operar el servicio (procesadores de pago, plataformas de envío de correo), bajo acuerdos de confidencialidad.'
    }, {
      heading: '5. Tus derechos',
      body: 'Tienes derecho a acceder, rectificar o solicitar la eliminación de tus datos personales en cualquier momento. Para ejercer estos derechos, escríbenos desde el correo con el que realizaste la compra.'
    }, {
      heading: '6. Seguridad',
      body: 'Aplicamos medidas razonables para proteger tu información. Sin embargo, ningún sistema en internet es 100% seguro, por lo que no podemos garantizar seguridad absoluta.'
    }, {
      heading: '7. Cambios a esta política',
      body: 'Podemos actualizar esta política periódicamente. La versión más reciente estará siempre disponible en este sitio.'
    }]
  }
};
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalKey | null>(null);
  useEffect(() => {
    if (openDoc) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [openDoc]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDoc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <>
      <footer className="bg-[#2d2d2d] text-white py-12" data-id="element-60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-61">
          <div className="text-center" data-id="element-62">
            <div className="mb-6" data-id="element-63">
              <h3 className="font-serif text-2xl md:text-3xl text-[#c9a961] mb-2" data-id="element-64">
                Legado Salud
              </h3>
              <p className="text-[#7a8a6b]" data-id="element-65">
                Tu guía premium de longevidad. Bienestar real para el cuerpo y
                la mente.
              </p>
            </div>

            <div className="h-px bg-[#7a8a6b] max-w-xs mx-auto mb-6" data-id="element-66"></div>

            <nav aria-label="Enlaces legales" className="mb-6" data-id="element-67">
              <ul className="flex items-center justify-center gap-6 text-sm" data-id="element-68">
                <li data-id="element-69">
                  <button onClick={() => setOpenDoc('terms')} className="text-[#c9a961] hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-70">
                    Términos y Condiciones
                  </button>
                </li>
                <li aria-hidden="true" className="text-[#7a8a6b]" data-id="element-71">
                  •
                </li>
                <li data-id="element-72">
                  <button onClick={() => setOpenDoc('privacy')} className="text-[#c9a961] hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-73">
                    Política de Privacidad
                  </button>
                </li>
              </ul>
            </nav>

            <p className="text-sm text-[#7a8a6b]" data-id="element-74">
              © 2026 Legado Salud. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-75">
        {openDoc && <motion.div key="legal-modal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={() => setOpenDoc(null)} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-76">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-[#faf6ee] w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl border border-[#c9a961]/40 flex flex-col overflow-hidden" data-id="element-77">
              <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 pb-4 border-b border-[#c9a961]/30 bg-[#faf6ee]" data-id="element-78">
                <h2 id="legal-modal-title" className="font-serif text-2xl md:text-3xl text-[#2d2d2d]" data-id="element-79">
                  {LEGAL_CONTENT[openDoc].title}
                </h2>
                <button onClick={() => setOpenDoc(null)} aria-label="Cerrar" className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[#2d2d2d] hover:bg-[#c9a961]/20 transition-colors" data-id="element-80">
                  <XIcon className="w-5 h-5" data-id="element-81" />
                </button>
              </div>

              <div className="overflow-y-auto px-6 sm:px-8 py-6 space-y-5 text-[#2d2d2d] leading-relaxed" data-id="element-82">
                {LEGAL_CONTENT[openDoc].sections.map((section, idx) => <div key={idx} data-id="element-83">
                    {section.heading && <h3 className="font-serif text-lg md:text-xl text-[#5d6e4f] mb-2" data-id="element-84">
                        {section.heading}
                      </h3>}
                    <p className="text-[15px] md:text-base text-[#2d2d2d]/90" data-id="element-85">
                      {section.body}
                    </p>
                  </div>)}
                <p className="text-xs text-[#7a8a6b] pt-4 border-t border-[#c9a961]/30" data-id="element-86">
                  Última actualización: mayo de 2026.
                </p>
              </div>

              <div className="px-6 sm:px-8 py-4 border-t border-[#c9a961]/30 bg-white/40 flex justify-end" data-id="element-87">
                <button onClick={() => setOpenDoc(null)} className="bg-[#5d6e4f] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#4a5940] transition-colors" data-id="element-88">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}